/**
 * AdditionalTableColumnsCanvas — podgląd klocka „Tabela dodatkowa" na płótnie kreatora formatek.
 *
 * Gdy host kreatora ma dostęp do danych tenanta (`useBuilderEnvironment().tenantData`) i klocek
 * ma wybraną tabelę, płótno pokazuje EDYTOR UKŁADU KOLUMN (`TableLayoutEditor`): nagłówki
 * kolumn wybranej tabeli z jednym wierszem przykładowym, a przy możliwości zapisu — przeciąganie,
 * szerokość, menu kolumny i panel „Kolumny". Każda akcja wysyła jedną łatkę `settings`
 * (`tableColumns` / `hiddenColumnIds`) przez `onSettingsChange`.
 *
 * Układ systemowy (`systemHideOnly`) przełącza edytor w tryb `hideOnly`: sam panel „Kolumny" z
 * checkboxami, a akcja wysyła wyłącznie `{ hiddenColumnIds }`. Kreator wyprowadza z tej maski
 * nakładkę tenanta (`tab_overrides.elementOverrides`) kluczowaną TABELĄ
 * (`additional_table:<tableSlug>`), więc kilka tabel dodatkowych na jednym układzie się nie zderza.
 *
 * W pozostałych przypadkach (brak providera środowiska — np. projektant karty kanban, kreator
 * modułów własnych; kreator osadzony w panelu ITE, gdzie endpointy tenanta odpowiadają 401;
 * tabela jeszcze niewybrana, niezaładowana albo nieistniejąca) płótno rysuje dotychczasowy
 * przerywany placeholder; przy błędzie odczytu pól albo braku tabeli placeholder podaje
 * powód (jak sekcja całej zakładki). Bez `onSettingsChange` (podgląd tylko do odczytu) edytor
 * jest podglądem bez kontrolek.
 *
 * Zapytania o dane tenanta (katalog tabel, pola tabeli) żyją WYŁĄCZNIE w komponencie
 * wewnętrznym (`useAdditionalTableColumnsModel`) — montowany dopiero po przejściu bramki, więc
 * host bez dostępu nie wysyła ich wcale.
 *
 * Korzeń (`className`/`style`): `LayoutElementRegistry.applyElementStyle` klonuje zwrócony element
 * i dokłada styl elementu, więc oba warianty (edytor i placeholder) przekazują te propsy na swój
 * korzeń DOM.
 */

import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { Table2 } from 'lucide-react';

import { TableLayoutEditor } from '@/components/embedded-table/TableLayoutEditor';
import { useBuilderEnvironment } from '@/components/fields/builder-environment';
import type { LayoutElement } from '@/components/fields/LayoutElementRegistry';
import { useAdditionalTableColumnsModel } from '@/lib/embedded-table/use-additional-table-columns-model';
import { cn } from '@/lib/utils';

interface PlaceholderProps {
  elementId: string;
  /** Pola wybranej tabeli jeszcze się ładują — placeholder dopisuje krótką informację. */
  loading?: boolean;
  /** Powód, dla którego nie ma edytora (błąd odczytu pól, brak tabeli) — krótki tekst pod nazwą klocka. */
  message?: string;
  className?: string;
  style?: CSSProperties;
}

/**
 * Przerywana ramka z ikoną i nazwą klocka — wspólny wygląd klocka tam, gdzie nie ma
 * edytora kolumn (płótno bez dostępu do danych tenanta, podgląd poza kreatorem).
 */
export function AdditionalTableViewPlaceholder({
  elementId,
  loading = false,
  message,
  className,
  style,
}: PlaceholderProps) {
  const { t } = useTranslation();
  return (
    <div
      className={cn(
        'rounded-lg border border-dashed border-border-secondary bg-bg-secondary p-4 text-center',
        className,
      )}
      style={style}
      data-testid={`additional-table-view-element-${elementId}`}
      data-element-type="additional_view"
    >
      <Table2 className="mx-auto h-5 w-5 text-text-muted" aria-hidden="true" />
      <p className="mt-1 text-xs font-medium text-text-secondary">
        {t('additionalTables.paletteLabel', { defaultValue: 'Tabela dodatkowa' })}
      </p>
      {loading && <p className="mt-1 text-xs text-text-muted">{t('common.loading')}</p>}
      {message !== undefined && <p className="mt-1 text-xs text-text-muted">{message}</p>}
    </div>
  );
}

interface CanvasProps {
  element: LayoutElement;
  /** Zapis łatki `settings` elementu; brak = podgląd tylko do odczytu. */
  onSettingsChange?: (patch: Record<string, unknown>) => void;
  /**
   * Układ systemowy — edytor w trybie `hideOnly` (tenant tylko ukrywa kolumny; łatka
   * `{ hiddenColumnIds }`). Bez `onSettingsChange` i tak zostaje podglądem bez kontrolek.
   */
  systemHideOnly?: boolean;
  className?: string;
  style?: CSSProperties;
}

interface EditorProps extends CanvasProps {
  tableSlug: string;
}

/** Edytor kolumn wybranej tabeli — jedyne miejsce z zapytaniami o dane tenanta. */
function AdditionalTableColumnsEditor({
  element,
  tableSlug,
  onSettingsChange,
  systemHideOnly = false,
  className,
  style,
}: EditorProps) {
  const { t } = useTranslation();
  const model = useAdditionalTableColumnsModel(tableSlug);

  if (model.status !== 'ready') {
    const message =
      model.status === 'error'
        ? t('additionalTables.loadError')
        : model.status === 'notFound'
          ? t('additionalTables.tableNotFound', { slug: tableSlug })
          : undefined;
    return (
      <AdditionalTableViewPlaceholder
        elementId={element.id}
        loading={model.status === 'pending'}
        message={message}
        className={className}
        style={style}
      />
    );
  }

  return (
    <TableLayoutEditor
      descriptors={model.descriptors}
      settings={element.settings}
      onChange={onSettingsChange}
      // Układ systemowy: tenant tylko ukrywa kolumny (maska `hiddenColumnIds`, patrz nagłówek pliku).
      mode={systemHideOnly ? 'hideOnly' : 'full'}
      title={model.title ?? tableSlug}
      // `min-w-0` — szeroka tabela przewija się w karcie (pasek nagłówków ma `overflow-x-auto`),
      // zamiast rozpychać rodzica.
      className={cn('min-w-0', className)}
      style={style}
      testIdPrefix={`additional-table-view-element-${element.id}`}
    />
  );
}

export function AdditionalTableColumnsCanvas({
  element,
  onSettingsChange,
  systemHideOnly = false,
  className,
  style,
}: CanvasProps) {
  const { tenantData } = useBuilderEnvironment();
  const rawSlug = element.settings?.tableSlug;
  const tableSlug = typeof rawSlug === 'string' ? rawSlug : '';

  if (!tenantData || tableSlug === '') {
    return (
      <AdditionalTableViewPlaceholder elementId={element.id} className={className} style={style} />
    );
  }

  return (
    <AdditionalTableColumnsEditor
      element={element}
      tableSlug={tableSlug}
      onSettingsChange={onSettingsChange}
      systemHideOnly={systemHideOnly}
      className={className}
      style={style}
    />
  );
}
